/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { CompetitorAction, MarketSegment, TechnologyAdoption } from '@/types/tycoon';
import type { Product } from '@/types/product';
// import type { Company } from '@/types/company';
import { ProductManager } from '../productManager';
import { ProductMetricsService } from '../ProductMetricsService';
import { aiDesignGenerator } from '../AIDesignGeneratorService';
import { seasonalLaunchService } from '../SeasonalLaunchService';
import { reviewGenerator } from './reviewGeneratorService';
import type { DesignConfiguration } from '@/stores/designStore';
import { GAME_CONFIG } from '../../constants/gameConfig';
import { SalesCalculationService } from './salesCalculationService';
import { MarketCapacityService } from './marketCapacityService';
import { componentReleaseService } from './componentReleaseService';
import { marketShareService } from './marketShareService';

interface CompetitorStrategy {
  competitorId: string;
  aggressiveness: number; // 0-1
  innovationFocus: number; // 0-1
  priceCompetitiveness: number; // 0-1
  marketingBudget: number;
  rdBudget: number;
  preferredSegments: string[];
  reactionSpeed: number; // months to react
  // Phase 2: Enhanced strategy attributes
  collaborationTendency: number; // 0-1
  riskTolerance: number; // 0-1
  marketExpansionFocus: number; // 0-1
  verticalIntegration: number; // 0-1
  partnerships: {
    supplierId: string;
    strength: number;
    exclusivity: boolean;
  }[];
  strategicAlliances: {
    partnerId: string;
    type: 'technology' | 'marketing' | 'distribution' | 'research';
    strength: number;
    duration: number;
  }[];
  // Market entry/exit
  marketStatus: 'inactive' | 'active' | 'exited';
  marketEntryYear?: number;
  marketExitYear?: number;
}

interface CompetitorProduct {
  id: string;
  competitorId: string;
  name: string;
  price: number;
  techScore: number;
  marketShare: number;
  launchDate: number;
  lifecycle: 'development' | 'launch' | 'growth' | 'maturity' | 'decline';
}

interface AIProductDevelopment {
  competitorId: string;
  designConfig: DesignConfiguration;
  phase: 'concept' | 'design' | 'development' | 'manufacturing';
  startDate: number;
  estimatedDuration: number;
  budget: number;
  confidence: number;
  qualityScore: number;
}

export class CompetitorAIService {
  private static instance: CompetitorAIService;
  private strategies: Map<string, CompetitorStrategy> = new Map();
  private competitorProducts: Map<string, CompetitorProduct[]> = new Map();
  private pendingActions: CompetitorAction[] = [];
  private marketIntelligence: Map<string, unknown> = new Map();
  private activeProductDevelopments: Map<string, AIProductDevelopment[]> = new Map();
  private currentGameDate: { day: number; month: number; year: number } = { day: GAME_CONFIG.STARTING_DAY, month: GAME_CONFIG.STARTING_MONTH, year: GAME_CONFIG.STARTING_YEAR };

  private constructor() {
    // Initialize AI design generator with default game year
    aiDesignGenerator.setGameYear(this.currentGameDate.year);
    this.initializeCompetitorStrategies();
    this.initializeCompetitorProducts();
  }

  public static getInstance(): CompetitorAIService {
    if (!CompetitorAIService.instance) {
      CompetitorAIService.instance = new CompetitorAIService();
    }
    return CompetitorAIService.instance;
  }

  // Phase 1: Basic Competitor Reactions
  public generateCompetitorReactions(playerProduct: Product, marketReception: number): CompetitorAction[] {
    const reactions: CompetitorAction[] = [];
    
    this.strategies.forEach((strategy, competitorId) => {
      // Skip inactive or exited companies
      if (strategy.marketStatus !== 'active') {
        return;
      }
      
      const reaction = this.analyzeCompetitorReaction(competitorId, playerProduct, marketReception);
      if (reaction) {
        reactions.push(reaction);
      }
    });

    return reactions;
  }

  public updateCompetitorStrategies(marketData: {
    playerMarketShare: number;
    marketSegments: MarketSegment[];
    technologyAdoptions: Map<string, TechnologyAdoption>;
  }): void {
    this.strategies.forEach((strategy, competitorId) => {
      // Skip inactive or exited companies
      if (strategy.marketStatus !== 'active') {
        return;
      }
      
      // Update strategy based on market performance
      this.adjustStrategy(competitorId, strategy, marketData);
      
      // Generate strategic actions
      const actions = this.generateStrategicActions(competitorId, strategy, marketData);
      this.pendingActions.push(...actions);
    });
  }

  public async executeCompetitorActions(_timeElapsed: number): Promise<CompetitorAction[]> {
    const currentTime = Date.now();
    const executedActions: CompetitorAction[] = [];

    // Simulate daily sales for all competitor products
    // await this.simulateCompetitorProductSales();

    // Check for market entry/exit
    const currentDate = this.getCurrentGameDate();
    this.checkMarketEntryExit(currentDate);

    // Process component releases for the current year (on January 1st)
    if (currentDate.month === 1 && currentDate.day === 1) {
      const releases = componentReleaseService.processYearlyReleases(currentDate.year);
      if (releases.length > 0) {
        console.log(`🔧 New components released in ${currentDate.year}:`, releases.length);
      }
    }
    
    // console.log(`🔍 Competitor AI: Checking seasonal launches for ${currentDate.month}/${currentDate.year}`);
    
    // Evaluate products for discontinuation (once per month on day 1)
    if (currentDate.day === 1) {
      // console.log(`🔄 Competitor AI: Evaluating products for discontinuation`);
      const discontinuationActions = await this.evaluateProductDiscontinuation();
      executedActions.push(...discontinuationActions);
    }
    
    this.strategies.forEach((strategy, competitorId) => {
      // Skip inactive or exited companies
      if (strategy.marketStatus !== 'active') {
        return;
      }
      const scheduledLaunch = seasonalLaunchService.shouldLaunchProduct(
        competitorId, 
        currentDate.year, 
        currentDate.month
      );
      
      if (scheduledLaunch) {
        // console.log(`📱 Competitor AI: ${competitorId} scheduled to launch ${scheduledLaunch.productName}`);
        
        // Launch the scheduled product
        this.launchSeasonalProduct(competitorId, scheduledLaunch);
        seasonalLaunchService.markLaunchCompleted(
          competitorId, 
          currentDate.year, 
          currentDate.month, 
          scheduledLaunch.productType
        );
        
        executedActions.push({
          competitorId,
          action: 'product_launch',
          probability: 1.0, // Seasonal launches are certain
          marketImpact: 0.5,
          timestamp: currentTime,
          productName: scheduledLaunch.productName
        });
      }
    });

    this.pendingActions = this.pendingActions.filter(action => {
      const timeSinceAction = currentTime - action.timestamp;
      const executionTime = this.getActionExecutionTime(action);

      if (timeSinceAction >= executionTime) {
        this.executeAction(action);
        executedActions.push(action);
        return false; // Remove from pending
      }
      return true; // Keep in pending
    });

    // Generate autonomous competitor actions
    this.strategies.forEach((strategy, competitorId) => {
      // Skip inactive or exited companies
      if (strategy.marketStatus !== 'active') {
        return;
      }
      const autonomousActions = this.generateAutonomousActions(competitorId, strategy);
      executedActions.push(...autonomousActions);
    });

    return executedActions;
  }

  // Phase 2: Advanced Competitive Intelligence
  public analyzeMarketOpportunities(segments: MarketSegment[]): Map<string, CompetitorAction[]> {
    const opportunities = new Map<string, CompetitorAction[]>();

    this.strategies.forEach((strategy, competitorId) => {
      const actions: CompetitorAction[] = [];

      segments.forEach(segment => {
        if (strategy.preferredSegments.includes(segment.id)) {
          // Identify underserved segments
          const segmentGap = this.identifySegmentGap(segment, competitorId);
          if (segmentGap > 0.3) {
            actions.push(this.createSegmentEntryAction(competitorId, segment));
          }
        }
      });

      if (actions.length > 0) {
        opportunities.set(competitorId, actions);
      }
    });

    return opportunities;
  }

  public predictCompetitorMoves(competitorId: string, _timeHorizon: number): CompetitorAction[] {
    const strategy = this.strategies.get(competitorId);
    if (!strategy) return [];

    const predictions: CompetitorAction[] = [];
    const currentProducts = this.competitorProducts.get(competitorId) || [];

    // Predict product lifecycle actions
    currentProducts.forEach(product => {
      if (product.lifecycle === 'maturity') {
        predictions.push({
          competitorId,
          action: 'product_launch',
          probability: 0.7,
          marketImpact: 0.4,
          timestamp: Date.now() + (6 * 30 * 24 * 60 * 60 * 1000) // 6 months
        });
      }
    });

    // Predict R&D investments
    if (strategy.innovationFocus > 0.6) {
      predictions.push({
        competitorId,
        action: 'rd_investment',
        rdBudget: strategy.rdBudget * 1.2,
        probability: 0.8,
        marketImpact: 0.3,
        timestamp: Date.now() + (3 * 30 * 24 * 60 * 60 * 1000) // 3 months
      });
    }

    return predictions;
  }

  // Phase 2: Complex Competitive Interactions
  public evaluateStrategicPartnerships(competitorId: string): CompetitorAction[] {
    const strategy = this.strategies.get(competitorId);
    if (!strategy || strategy.collaborationTendency < 0.4) return [];

    const partnershipActions: CompetitorAction[] = [];
    
    // Evaluate technology partnerships
    this.strategies.forEach((partnerStrategy, partnerId) => {
      if (partnerId === competitorId) return;
      
      const synergy = this.calculatePartnershipSynergy(strategy, partnerStrategy);
      if (synergy > 0.6) {
        partnershipActions.push({
          competitorId,
          action: 'rd_investment',
          targetCompanyId: partnerId,
          rdBudget: strategy.rdBudget * 0.3, // 30% of R&D budget for partnership
          probability: synergy * strategy.collaborationTendency,
          marketImpact: 0.4,
          timestamp: Date.now()
        });
      }
    });

    return partnershipActions;
  }

  public simulateCompetitiveEscalation(marketData: {
    playerActions: unknown[];
    marketShare: number;
    productLaunches: Product[];
  }): CompetitorAction[] {
    const escalationActions: CompetitorAction[] = [];

    this.strategies.forEach((strategy, competitorId) => {
      // Detect competitive threats
      const threatLevel = this.assessOverallThreatLevel(competitorId, marketData);
      
      if (threatLevel > 0.7) {
        // High threat triggers escalation
        const escalationAction = this.generateEscalationResponse(competitorId, strategy, threatLevel);
        if (escalationAction) {
          escalationActions.push(escalationAction);
        }
      }
    });

    return escalationActions;
  }

  public calculateMarketDominanceShifts(): Map<string, {
    currentPosition: number;
    projectedPosition: number;
    dominanceGain: number;
    vulnerabilities: string[];
  }> {
    const dominanceAnalysis = new Map();

    this.strategies.forEach((strategy, competitorId) => {
      const products = this.competitorProducts.get(competitorId) || [];
      const currentPosition = this.calculateMarketPosition(competitorId, products);
      const projectedPosition = this.projectFuturePosition(competitorId, strategy, products);
      const dominanceGain = projectedPosition - currentPosition;
      const vulnerabilities = this.identifyVulnerabilities(competitorId, strategy, products);

      dominanceAnalysis.set(competitorId, {
        currentPosition,
        projectedPosition,
        dominanceGain,
        vulnerabilities
      });
    });

    return dominanceAnalysis;
  }

  public simulateSupplyChainWarfare(): CompetitorAction[] {
    const supplyChainActions: CompetitorAction[] = [];

    this.strategies.forEach((strategy, competitorId) => {
      if (strategy.verticalIntegration > 0.6 && strategy.aggressiveness > 0.7) {
        // Aggressive vertical integration strategy
        const exclusiveSupplierAction = this.createExclusiveSupplierAction(competitorId, strategy);
        if (exclusiveSupplierAction) {
          supplyChainActions.push(exclusiveSupplierAction);
        }
      }
    });

    return supplyChainActions;
  }

  public evaluateCollaborativeInnovation(): Array<{
    participants: string[];
    technology: string;
    marketImpact: number;
    timeline: number;
    probability: number;
  }> {
    const collaborations: Array<{
      participants: string[];
      technology: string;
      marketImpact: number;
      timeline: number;
      probability: number;
    }> = [];

    // Find potential collaboration clusters
    const collaborativeCompetitors = Array.from(this.strategies.entries())
      .filter(([_, strategy]) => strategy.collaborationTendency > 0.6);

    // Generate collaboration scenarios
    for (let i = 0; i < collaborativeCompetitors.length; i++) {
      for (let j = i + 1; j < collaborativeCompetitors.length; j++) {
        const [comp1Id, strategy1] = collaborativeCompetitors[i];
        const [comp2Id, strategy2] = collaborativeCompetitors[j];
        
        const collaboration = this.evaluateTechnologyCollaboration(
          comp1Id, strategy1, comp2Id, strategy2
        );
        
        if (collaboration.probability > 0.5) {
          collaborations.push(collaboration);
        }
      }
    }

    return collaborations.sort((a, b) => b.probability - a.probability);
  }

  // Phase 3: Market Consolidation and Acquisitions
  public evaluateAcquisitionTargets(competitorId: string): CompetitorAction[] {
    const strategy = this.strategies.get(competitorId);
    if (!strategy || strategy.aggressiveness < 0.7) return [];

    const acquisitionActions: CompetitorAction[] = [];
    
    // Identify weak competitors for acquisition
    this.strategies.forEach((targetStrategy, targetId) => {
      if (targetId === competitorId) return;
      
      const acquisitionProbability = this.calculateAcquisitionProbability(
        competitorId, 
        targetId, 
        strategy, 
        targetStrategy
      );

      if (acquisitionProbability > 0.3) {
        acquisitionActions.push({
          competitorId,
          action: 'acquisition',
          targetCompanyId: targetId,
          probability: acquisitionProbability,
          marketImpact: 0.6,
          timestamp: Date.now()
        });
      }
    });

    return acquisitionActions;
  }

  // Private implementation methods
  private analyzeCompetitorReaction(
    competitorId: string, 
    playerProduct: Product, 
    marketReception: number
  ): CompetitorAction | null {
    const strategy = this.strategies.get(competitorId);
    if (!strategy) return null;

    // Calculate threat level from player product
    const threatLevel = this.calculateThreatLevel(playerProduct, competitorId, marketReception);
    
    if (threatLevel < 0.3) return null; // No significant threat

    // Determine reaction type based on strategy and threat
    const reactionType = this.determineReactionType(strategy, threatLevel);
    const reactionDelay = strategy.reactionSpeed * (1 + Math.random() * 0.5); // Add randomness

    return {
      competitorId,
      action: reactionType,
      probability: Math.min(threatLevel * strategy.aggressiveness, 0.9),
      marketImpact: threatLevel * 0.5,
      timestamp: Date.now() + (reactionDelay * 30 * 24 * 60 * 60 * 1000) // Convert months to ms
    };
  }

  private calculateThreatLevel(product: Product, competitorId: string, marketReception: number): number {
    let threat = marketReception * 0.5; // Base threat from market reception
    
    const competitorProducts = this.competitorProducts.get(competitorId) || [];
    const competingProducts = competitorProducts.filter(p => 
      Math.abs(p.price - product.price) < product.price * 0.3 // Similar price range
    );

    // Higher threat if competing in same segment
    if (competingProducts.length > 0) {
      threat *= 1.5;
    }

    // Technology innovation threat
    const avgTechScore = product.techScore;
    const competitorAvgTech = competingProducts.reduce((sum, p) => sum + p.techScore, 0) / Math.max(competingProducts.length, 1);
    
    if (avgTechScore > competitorAvgTech + 10) {
      threat *= 1.3;
    }

    return Math.min(threat, 1.0);
  }

  private determineReactionType(strategy: CompetitorStrategy, threatLevel: number): CompetitorAction['action'] {
    // High innovation focus competitors launch competing products
    if (strategy.innovationFocus > 0.7 && threatLevel > 0.6) {
      return 'product_launch';
    }

    // Price-competitive competitors adjust pricing
    if (strategy.priceCompetitiveness > 0.7 && threatLevel > 0.4) {
      return 'price_change';
    }

    // Marketing-focused competitors increase campaigns
    // if (strategy.marketingBudget > 50000 && threatLevel > 0.3) {
    //   return 'marketing_campaign';
    // }

    // Default to R&D investment
    return 'rd_investment';
  }

  private adjustStrategy(
    competitorId: string, 
    strategy: CompetitorStrategy, 
    marketData: any
  ): void {
    // Adjust aggressiveness based on market performance
    const products = this.competitorProducts.get(competitorId) || [];
    const avgMarketShare = products.reduce((sum, p) => sum + p.marketShare, 0) / Math.max(products.length, 1);

    if (avgMarketShare < 0.05) {
      strategy.aggressiveness = Math.min(strategy.aggressiveness + 0.1, 1.0);
    } else if (avgMarketShare > 0.2) {
      strategy.aggressiveness = Math.max(strategy.aggressiveness - 0.05, 0.3);
    }

    // Adjust innovation focus based on technology trends
    const emergingTech = Array.from(marketData.technologyAdoptions.values())
      .filter(adoption => adoption.stage === 'early_adopters').length;
    
    if (emergingTech > 2) {
      strategy.innovationFocus = Math.min(strategy.innovationFocus + 0.1, 1.0);
    }
  }

  private generateStrategicActions(
    competitorId: string, 
    strategy: CompetitorStrategy, 
    marketData: any
  ): CompetitorAction[] {
    const actions: CompetitorAction[] = [];
    
    // Generate product development actions
    if (strategy.innovationFocus > 0.5 && Math.random() < 0.3) {
      actions.push({
        competitorId,
        action: 'product_launch',
        probability: strategy.innovationFocus,
        marketImpact: 0.4,
        timestamp: Date.now() + (6 + Math.random() * 6) * 30 * 24 * 60 * 60 * 1000 // 6-12 months
      });
    }

    // Generate R&D investment actions
    if (strategy.rdBudget > 0 && Math.random() < 0.4) {
      actions.push({
        competitorId,
        action: 'rd_investment',
        rdBudget: strategy.rdBudget * (0.8 + Math.random() * 0.4),
        probability: 0.8,
        marketImpact: 0.2,
        timestamp: Date.now()
      });
    }

    return actions;
  }

  private generateAutonomousActions(competitorId: string, strategy: CompetitorStrategy): CompetitorAction[] {
    const actions: CompetitorAction[] = [];
    
    // Periodic marketing campaigns
    // if (Math.random() < 0.1 && strategy.marketingBudget > 0) {
    //   actions.push({
    //     competitorId,
    //     action: 'marketing_campaign',
    //     campaignBudget: strategy.marketingBudget * (0.5 + Math.random() * 0.5),
    //     probability: 0.9,
    //     marketImpact: 0.15,
    //     timestamp: Date.now()
    //   });
    // }

    return actions;
  }

  private executeAction(action: CompetitorAction): void {
    const competitorId = action.competitorId;
    
    switch (action.action) {
      case 'product_launch':
        this.launchCompetitorProduct(competitorId);
        break;
      case 'price_change':
        this.adjustCompetitorPricing(competitorId, action);
        break;
      // case 'marketing_campaign':
      //   this.executeMarketingCampaign(competitorId, action);
      //   break;
      // case 'rd_investment':
      //   this.investInRD(competitorId, action);
      //   break;
      // case 'acquisition':
      //   this.executeAcquisition(competitorId, action);
      //   break;
    }
  }

  private async launchCompetitorProduct(competitorId: string): Promise<void> {
    const strategy = this.strategies.get(competitorId);
    if (!strategy) return;

    try {
      // Gather market context for pricing decisions
      const marketContext = await this.gatherMarketContext();
      
      // Generate AI design configuration with market awareness
      const designConfig = aiDesignGenerator.generateDesignConfiguration({
        id: competitorId,
        innovationFocus: strategy.innovationFocus,
        priceCompetitiveness: strategy.priceCompetitiveness,
        preferredSegments: strategy.preferredSegments,
      }, marketContext);

      // Calculate metrics using the same system as player products
      const selectedComponents = {
        os: designConfig.os,
        cpu: designConfig.cpu,
        gpu: designConfig.gpu,
        ram: designConfig.ram,
        storage: designConfig.storage,
        network: designConfig.network,
        camera: designConfig.camera,
        battery: designConfig.battery,
      };

      const productionCost = ProductMetricsService.calculateTotalCost(selectedComponents);
      const techScore = ProductMetricsService.calculateTechScore(selectedComponents);
      const marketAppeal = ProductMetricsService.calculateValueScore(techScore, designConfig.price);

      // Create temporary product for review generation
      const tempProduct = {
        techScore,
        marketAppeal,
        price: designConfig.price
      } as Product;

      // Generate reviews for competitor product
      const reviews = reviewGenerator.generateReviews(tempProduct);
      const averageRating = reviewGenerator.calculateAverageRating(reviews);

      // Create a real Product object following the same schema as player products
      const productData: Omit<Product, 'id'> = {
        name: designConfig.name,
        description: `AI-generated product by ${competitorId}`,
        developmentCost: productionCost * 2, // Development costs are typically 2x production
        productionCost,
        price: designConfig.price,
        techScore,
        marketAppeal,
        status: 'released',
        designConfig: {
          name: designConfig.name,
          price: designConfig.price,
          color: designConfig.color,
          material: designConfig.material,
          components: selectedComponents,
          design: {
            buttonColor: designConfig.buttonColor,
            hasButtons: designConfig.hasButtons,
            screenBorderColor: designConfig.screenBorderColor,
            cameraStyle: designConfig.cameraStyle,
            cameraColor: designConfig.cameraColor,
            cameraPosition: designConfig.cameraPosition,
            buttonPositions: designConfig.buttonPositions,
            phoneSize: designConfig.phoneSize,
            screenSize: designConfig.screenSize,
            cornerRadius: designConfig.cornerRadius,
          },
          techScore,
          marketAppeal,
        },
        metadata: {
          companyId: competitorId,
          designDate: this.getCurrentGameDate(),
          releaseDate: this.getCurrentGameDate(), // Use current game date for initial products
          isReleased: true, // AI competitors auto-release their products
        },
        salesMetrics: {
          unitsSold: 0,
          revenue: 0,
          averageRating,
          marketShare: 0.01, // Start with 1% market share
        },
        reviews,
      };

      // Store the product in the database using ProductManager
      const result = await ProductManager.createProduct(productData);

      if (result.success) {
        // Invalidate market share cache so it recalculates with new competitor
        marketShareService.invalidateCache();

        // console.log(`✅ AI Competitor ${competitorId} launched product: ${designConfig.name} (ID: ${result.data?.id})`);

        // Update internal tracking for backward compatibility
        const competitorProduct: CompetitorProduct = {
          id: result.data!.id,
          competitorId,
          name: designConfig.name,
          price: designConfig.price,
          techScore,
          marketShare: 0.01,
          launchDate: Date.now(),
          lifecycle: 'launch'
        };

        const products = this.competitorProducts.get(competitorId) || [];
        products.push(competitorProduct);
        this.competitorProducts.set(competitorId, products);
      } else {
        console.error(`❌ Failed to launch AI product for ${competitorId}:`, result.error);
      }
    } catch (error) {
      console.error(`❌ Error launching AI product for ${competitorId}:`, error);
    }
  }

  private adjustCompetitorPricing(competitorId: string, action: CompetitorAction): void {
    const products = this.competitorProducts.get(competitorId) || [];
    const priceMultiplier = action.newPrice ? action.newPrice / products[0]?.price || 1 : 0.95;
    
    products.forEach(product => {
      product.price *= priceMultiplier;
    });
  }

  private executeMarketingCampaign(competitorId: string, action: CompetitorAction): void {
    const products = this.competitorProducts.get(competitorId) || [];
    const marketShareBoost = (action.campaignBudget || 0) / 100000 * 0.01; // 1% per 100k budget
    
    products.forEach(product => {
      product.marketShare = Math.min(product.marketShare + marketShareBoost, 0.3);
    });
  }

  private investInRD(competitorId: string, action: CompetitorAction): void {
    const strategy = this.strategies.get(competitorId);
    if (!strategy) return;

    // R&D investment improves future product tech scores
    const rdBoost = (action.rdBudget || 0) / 1000000 * 5; // 5 points per 1M investment
    strategy.innovationFocus = Math.min(strategy.innovationFocus + rdBoost / 100, 1.0);
  }

  private executeAcquisition(competitorId: string, action: CompetitorAction): void {
    if (!action.targetCompanyId) return;
    
    // Remove target company and merge their products
    const targetProducts = this.competitorProducts.get(action.targetCompanyId) || [];
    const acquirerProducts = this.competitorProducts.get(competitorId) || [];
    
    this.competitorProducts.set(competitorId, [...acquirerProducts, ...targetProducts]);
    this.competitorProducts.delete(action.targetCompanyId);
    this.strategies.delete(action.targetCompanyId);
  }

  // Helper methods
  private getActionExecutionTime(action: CompetitorAction): number {
    const executionTimes: Record<CompetitorAction['action'], number> = {
      product_launch: 6 * 30 * 24 * 60 * 60 * 1000, // 6 months
      price_change: 7 * 24 * 60 * 60 * 1000, // 1 week
      marketing_campaign: 30 * 24 * 60 * 60 * 1000, // 1 month
      rd_investment: 0, // Immediate
      acquisition: 3 * 30 * 24 * 60 * 60 * 1000, // 3 months
      product_discontinuation: 0 // Immediate
    };
    
    return executionTimes[action.action] || 0;
  }

  private identifySegmentGap(segment: MarketSegment, competitorId: string): number {
    // Placeholder for segment analysis
    return Math.random() * 0.5;
  }

  private createSegmentEntryAction(competitorId: string, segment: MarketSegment): CompetitorAction {
    return {
      competitorId,
      action: 'product_launch',
      probability: 0.6,
      marketImpact: 0.3,
      timestamp: Date.now() + (8 * 30 * 24 * 60 * 60 * 1000) // 8 months
    };
  }

  private calculateAcquisitionProbability(
    acquirerId: string,
    targetId: string,
    acquirerStrategy: CompetitorStrategy,
    targetStrategy: CompetitorStrategy
  ): number {
    const acquirerProducts = this.competitorProducts.get(acquirerId) || [];
    const targetProducts = this.competitorProducts.get(targetId) || [];
    
    const acquirerShare = acquirerProducts.reduce((sum, p) => sum + p.marketShare, 0);
    const targetShare = targetProducts.reduce((sum, p) => sum + p.marketShare, 0);
    
    // Higher probability if acquirer is much larger
    if (acquirerShare > targetShare * 3) {
      return 0.7 * acquirerStrategy.aggressiveness;
    }
    
    return 0.2 * acquirerStrategy.aggressiveness;
  }

  private generateProductName(competitorId: string): string {
    const prefixes = ['Pro', 'Ultra', 'Max', 'Elite', 'Prime', 'Fusion', 'Nova', 'Apex'];
    const suffixes = ['X', 'Plus', 'Edge', 'Turbo', 'One', 'S', 'Pro', 'Elite'];
    
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    const number = Math.floor(Math.random() * 20) + 10;
    
    return `${competitorId.slice(0, 4)} ${prefix} ${number}${suffix}`;
  }

  private calculateCompetitorPrice(competitorId: string, strategy: CompetitorStrategy): number {
    const basePrice = 500;
    const innovationMultiplier = 1 + strategy.innovationFocus * 0.8;
    const competitivenessMultiplier = 1 - strategy.priceCompetitiveness * 0.3;
    
    return Math.floor(basePrice * innovationMultiplier * competitivenessMultiplier);
  }

  private calculateCompetitorTechScore(competitorId: string, strategy: CompetitorStrategy): number {
    const baseTechScore = 60;
    const innovationBonus = strategy.innovationFocus * 25;
    const randomVariation = (Math.random() - 0.5) * 10;
    
    return Math.max(30, Math.min(baseTechScore + innovationBonus + randomVariation, 95));
  }

  // Phase 2: Helper methods for complex competitive interactions
  private calculatePartnershipSynergy(strategy1: CompetitorStrategy, strategy2: CompetitorStrategy): number {
    let synergy = 0;

    // Complementary innovation focus
    const innovationGap = Math.abs(strategy1.innovationFocus - strategy2.innovationFocus);
    if (innovationGap > 0.3) {
      synergy += 0.3; // Complementary strengths
    }

    // Shared market segments
    const commonSegments = strategy1.preferredSegments.filter(seg => 
      strategy2.preferredSegments.includes(seg)).length;
    synergy += commonSegments * 0.2;

    // Similar collaboration tendency
    const collaborationAlignment = 1 - Math.abs(strategy1.collaborationTendency - strategy2.collaborationTendency);
    synergy += collaborationAlignment * 0.3;

    // Risk tolerance compatibility
    const riskAlignment = 1 - Math.abs(strategy1.riskTolerance - strategy2.riskTolerance);
    synergy += riskAlignment * 0.2;

    return Math.min(synergy, 1.0);
  }

  private assessOverallThreatLevel(competitorId: string, marketData: any): number {
    let threatLevel = 0;

    // Player market share threat
    if (marketData.marketShare > 0.15) {
      threatLevel += marketData.marketShare * 2;
    }

    // Player product launches threat
    const recentLaunches = marketData.productLaunches.filter((product: Product) => {
      const monthsAgo = (Date.now() - Date.now()) / (30 * 24 * 60 * 60 * 1000);
      return monthsAgo < 6; // Last 6 months
    });
    threatLevel += recentLaunches.length * 0.2;

    // Player aggressive actions threat
    const aggressiveActions = marketData.playerActions.filter((action: any) => 
      action.type === 'price_war' || action.type === 'market_expansion');
    threatLevel += aggressiveActions.length * 0.3;

    return Math.min(threatLevel, 1.0);
  }

  private generateEscalationResponse(competitorId: string, strategy: CompetitorStrategy, threatLevel: number): CompetitorAction | null {
    if (strategy.aggressiveness < 0.5) return null;

    const escalationTypes: CompetitorAction['action'][] = [
      // 'price_change', 'marketing_campaign', 'product_launch', 'rd_investment'
      'price_change', 'product_launch'
    ];

    const selectedAction = escalationTypes[Math.floor(Math.random() * escalationTypes.length)];
    const intensity = threatLevel * strategy.aggressiveness;

    return {
      competitorId,
      action: selectedAction,
      probability: intensity,
      marketImpact: intensity * 0.8,
      timestamp: Date.now() + (30 * 24 * 60 * 60 * 1000), // 1 month delay
      ...(selectedAction === 'price_change' && { newPrice: 0.85 }), // 15% price cut
      // ...(selectedAction === 'marketing_campaign' && { campaignBudget: strategy.marketingBudget * intensity }),
      // ...(selectedAction === 'rd_investment' && { rdBudget: strategy.rdBudget * intensity })
    };
  }

  private calculateMarketPosition(competitorId: string, products: CompetitorProduct[]): number {
    const totalMarketShare = products.reduce((sum, product) => sum + product.marketShare, 0);
    const avgTechScore = products.reduce((sum, product) => sum + product.techScore, 0) / Math.max(products.length, 1);
    const recentProducts = products.filter(product => {
      const monthsOld = (Date.now() - product.launchDate) / (30 * 24 * 60 * 60 * 1000);
      return monthsOld < 12;
    }).length;

    return (totalMarketShare * 0.5) + (avgTechScore / 100 * 0.3) + (recentProducts * 0.05);
  }

  private projectFuturePosition(competitorId: string, strategy: CompetitorStrategy, products: CompetitorProduct[]): number {
    const currentPosition = this.calculateMarketPosition(competitorId, products);
    
    let projectedGrowth = 0;

    // Innovation focus impact
    projectedGrowth += strategy.innovationFocus * 0.1;

    // R&D budget impact
    projectedGrowth += (strategy.rdBudget / 1000000) * 0.05; // 5% per million

    // Aggressiveness impact
    projectedGrowth += strategy.aggressiveness * 0.08;

    // Risk tolerance impact
    projectedGrowth += strategy.riskTolerance * 0.03;

    // Partnership benefits
    projectedGrowth += strategy.partnerships.length * 0.02;
    projectedGrowth += strategy.strategicAlliances.length * 0.03;

    return Math.min(currentPosition + projectedGrowth, 1.0);
  }

  private identifyVulnerabilities(competitorId: string, strategy: CompetitorStrategy, products: CompetitorProduct[]): string[] {
    const vulnerabilities: string[] = [];

    // Technology lag
    const avgTechScore = products.reduce((sum, product) => sum + product.techScore, 0) / Math.max(products.length, 1);
    if (avgTechScore < 60) {
      vulnerabilities.push('technology_lag');
    }

    // Price competitiveness
    if (strategy.priceCompetitiveness < 0.4) {
      vulnerabilities.push('price_uncompetitive');
    }

    // Market concentration
    const segmentCount = strategy.preferredSegments.length;
    if (segmentCount < 2) {
      vulnerabilities.push('market_concentration');
    }

    // Innovation weakness
    if (strategy.innovationFocus < 0.5) {
      vulnerabilities.push('innovation_weakness');
    }

    // Partnership dependency
    const exclusivePartnerships = strategy.partnerships.filter(p => p.exclusivity).length;
    if (exclusivePartnerships > 2) {
      vulnerabilities.push('partnership_dependency');
    }

    // Financial strain
    const totalBudget = strategy.marketingBudget + strategy.rdBudget;
    if (totalBudget < 500000) {
      vulnerabilities.push('financial_constraints');
    }

    return vulnerabilities;
  }

  private createExclusiveSupplierAction(competitorId: string, strategy: CompetitorStrategy): CompetitorAction | null {
    if (strategy.partnerships.length === 0) return null;

    const targetSupplier = strategy.partnerships[Math.floor(Math.random() * strategy.partnerships.length)];
    
    return {
      competitorId,
      action: 'rd_investment',
      targetCompanyId: targetSupplier.supplierId,
      rdBudget: strategy.rdBudget * 0.4, // 40% of R&D budget for exclusive partnership
      probability: strategy.verticalIntegration * strategy.aggressiveness,
      marketImpact: 0.5,
      timestamp: Date.now()
    };
  }

  private evaluateTechnologyCollaboration(
    comp1Id: string, strategy1: CompetitorStrategy, 
    comp2Id: string, strategy2: CompetitorStrategy
  ): {
    participants: string[];
    technology: string;
    marketImpact: number;
    timeline: number;
    probability: number;
  } {
    const technologies = [
      'next_gen_processors', 'advanced_batteries', 'ar_displays', 
      'quantum_encryption', '6g_modems', 'neural_interfaces'
    ];
    
    const selectedTech = technologies[Math.floor(Math.random() * technologies.length)];
    const synergy = this.calculatePartnershipSynergy(strategy1, strategy2);
    
    // Combined innovation capability
    const innovationCapability = (strategy1.innovationFocus + strategy2.innovationFocus) / 2;
    
    // Risk sharing benefits
    const riskMitigation = (strategy1.riskTolerance + strategy2.riskTolerance) / 2;
    
    const probability = synergy * innovationCapability * riskMitigation;
    const marketImpact = innovationCapability * 0.8;
    const timeline = Math.ceil(24 / innovationCapability); // months

    return {
      participants: [comp1Id, comp2Id],
      technology: selectedTech,
      marketImpact,
      timeline,
      probability
    };
  }

  private initializeCompetitorStrategies(): void {
    const competitors = [
      {
        id: 'apple',
        aggressiveness: 0.6,
        innovationFocus: 0.9,
        priceCompetitiveness: 0.3,
        marketingBudget: 2000000,
        rdBudget: 5000000,
        preferredSegments: ['premium', 'flagship'],
        reactionSpeed: 6, // Apple takes time to respond
        // Phase 2: Enhanced attributes
        collaborationTendency: 0.2,
        riskTolerance: 0.8,
        marketExpansionFocus: 0.7,
        verticalIntegration: 0.9,
        partnerships: [
          { supplierId: 'sup-001', strength: 0.9, exclusivity: true },
          { supplierId: 'sup-002', strength: 0.8, exclusivity: false }
        ],
        strategicAlliances: [],
        marketStatus: 'inactive' as const,
        marketEntryYear: 2007,
      },
      {
        id: 'samsung',
        aggressiveness: 0.8,
        innovationFocus: 0.8,
        priceCompetitiveness: 0.6,
        marketingBudget: 1500000,
        rdBudget: 4000000,
        preferredSegments: ['mainstream', 'premium', 'flagship'],
        reactionSpeed: 3,
        // Phase 2: Enhanced attributes
        collaborationTendency: 0.4,
        riskTolerance: 0.7,
        marketExpansionFocus: 0.9,
        verticalIntegration: 0.8,
        partnerships: [
          { supplierId: 'sup-002', strength: 0.8, exclusivity: false },
          { supplierId: 'sup-003', strength: 0.9, exclusivity: true }
        ],
        strategicAlliances: [],
        marketStatus: 'active' as const, // Samsung starts active
      },
      {
        id: 'google',
        aggressiveness: 0.5,
        innovationFocus: 0.9,
        priceCompetitiveness: 0.7,
        marketingBudget: 800000,
        rdBudget: 3000000,
        preferredSegments: ['mainstream', 'premium'],
        reactionSpeed: 4,
        // Phase 2: Enhanced attributes
        collaborationTendency: 0.6,
        riskTolerance: 0.6,
        marketExpansionFocus: 0.5,
        verticalIntegration: 0.3,
        partnerships: [
          { supplierId: 'sup-001', strength: 0.7, exclusivity: false },
          { supplierId: 'sup-003', strength: 0.6, exclusivity: false }
        ],
        strategicAlliances: [],
        marketStatus: 'inactive' as const,
        marketEntryYear: 2008,
      },
      {
        id: 'lg',
        aggressiveness: 0.7,
        innovationFocus: 0.7,
        priceCompetitiveness: 0.8,
        marketingBudget: 1000000,
        rdBudget: 2500000,
        preferredSegments: ['budget', 'mainstream', 'premium'],
        reactionSpeed: 4,
        collaborationTendency: 0.5,
        riskTolerance: 0.6,
        marketExpansionFocus: 0.8,
        verticalIntegration: 0.6,
        partnerships: [
          { supplierId: 'sup-002', strength: 0.7, exclusivity: false },
          { supplierId: 'sup-004', strength: 0.8, exclusivity: false }
        ],
        strategicAlliances: [],
        marketStatus: 'active' as const,
        marketExitYear: 2021,
      },
      {
        id: 'nokia',
        aggressiveness: 0.5,
        innovationFocus: 0.6,
        priceCompetitiveness: 0.9,
        marketingBudget: 1200000,
        rdBudget: 2000000,
        preferredSegments: ['budget', 'mainstream'],
        reactionSpeed: 5,
        collaborationTendency: 0.3,
        riskTolerance: 0.4,
        marketExpansionFocus: 0.9,
        verticalIntegration: 0.5,
        partnerships: [
          { supplierId: 'sup-003', strength: 0.6, exclusivity: false },
          { supplierId: 'sup-005', strength: 0.7, exclusivity: false }
        ],
        strategicAlliances: [],
        marketStatus: 'active' as const,
      },
      {
        id: 'palm',
        aggressiveness: 0.6,
        innovationFocus: 0.8,
        priceCompetitiveness: 0.5,
        marketingBudget: 600000,
        rdBudget: 1500000,
        preferredSegments: ['premium', 'flagship'],
        reactionSpeed: 4,
        collaborationTendency: 0.7,
        riskTolerance: 0.8,
        marketExpansionFocus: 0.4,
        verticalIntegration: 0.3,
        partnerships: [
          { supplierId: 'sup-001', strength: 0.5, exclusivity: false }
        ],
        strategicAlliances: [],
        marketStatus: 'active' as const,
        marketExitYear: 2012,
      },
      {
        id: 'motorola',
        aggressiveness: 0.6,
        innovationFocus: 0.7,
        priceCompetitiveness: 0.7,
        marketingBudget: 1100000,
        rdBudget: 2200000,
        preferredSegments: ['mainstream', 'premium'],
        reactionSpeed: 4,
        collaborationTendency: 0.4,
        riskTolerance: 0.5,
        marketExpansionFocus: 0.7,
        verticalIntegration: 0.6,
        partnerships: [
          { supplierId: 'sup-002', strength: 0.6, exclusivity: false },
          { supplierId: 'sup-004', strength: 0.7, exclusivity: false }
        ],
        strategicAlliances: [],
        marketStatus: 'active' as const,
        marketExitYear: 2017,
      },
      {
        id: 'microsoft',
        aggressiveness: 0.7,
        innovationFocus: 0.8,
        priceCompetitiveness: 0.6,
        marketingBudget: 1800000,
        rdBudget: 3500000,
        preferredSegments: ['mainstream', 'premium'],
        reactionSpeed: 5,
        collaborationTendency: 0.8,
        riskTolerance: 0.7,
        marketExpansionFocus: 0.6,
        verticalIntegration: 0.4,
        partnerships: [
          { supplierId: 'sup-001', strength: 0.7, exclusivity: false },
          { supplierId: 'sup-003', strength: 0.8, exclusivity: false }
        ],
        strategicAlliances: [],
        marketStatus: 'active' as const,
      },
      {
        id: 'rim',
        aggressiveness: 0.5,
        innovationFocus: 0.6,
        priceCompetitiveness: 0.5,
        marketingBudget: 900000,
        rdBudget: 1800000,
        preferredSegments: ['premium', 'flagship'],
        reactionSpeed: 6,
        collaborationTendency: 0.2,
        riskTolerance: 0.3,
        marketExpansionFocus: 0.5,
        verticalIntegration: 0.7,
        partnerships: [
          { supplierId: 'sup-002', strength: 0.6, exclusivity: false }
        ],
        strategicAlliances: [],
        marketStatus: 'active' as const,
        marketExitYear: 2014,
      }
    ];

    competitors.forEach(comp => {
      const strategy: CompetitorStrategy = {
        ...comp,
        competitorId: comp.id
      };
      this.strategies.set(comp.id, strategy);
    });
  }

  private initializeCompetitorProducts(): void {
    // Start with no products - competitors will launch products according to their schedules
    this.strategies.forEach((strategy, competitorId) => {
      this.competitorProducts.set(competitorId, []);
    });
  }

  // Public getters
  public getCompetitorStrategies(): Map<string, CompetitorStrategy> {
    return this.strategies;
  }

  public getCompetitorProducts(): Map<string, CompetitorProduct[]> {
    return this.competitorProducts;
  }

  public getPendingActions(): CompetitorAction[] {
    return this.pendingActions;
  }

  /**
   * Mark an action as processed and remove it from pending actions
   */
  public markActionProcessed(actionId: string): void {
    this.pendingActions = this.pendingActions.filter(action => action.id !== actionId);
  }

  /**
   * Set the current game date - called by the game simulation
   */
  public setCurrentGameDate(date: { day: number; month: number; year: number }): void {
    const oldDate = { ...this.currentGameDate };
    this.currentGameDate = date;

    // Update AI design generator with current game year for proper product naming
    aiDesignGenerator.setGameYear(date.year);

    // Process component releases when year changes
    if (date.month === 1 && date.day === 1) {
      componentReleaseService.processYearlyReleases(date.year);
    }
    
    // Debug: Log date changes for first day of month
    if (date.day === 1 && (oldDate.month !== date.month || oldDate.year !== date.year)) {
      // console.log(`🗓️ Competitor AI: Game date updated to ${date.month}/${date.year} (was ${oldDate.month}/${oldDate.year})`);
    }
  }

  /**
   * Get current game date - now properly synchronized with game time
   */
  private getCurrentGameDate(): { day: number; month: number; year: number } {
    return this.currentGameDate;
  }
  
  /**
   * Check for market entry and exit based on the current game date
   */
  private checkMarketEntryExit(currentDate: { day: number; month: number; year: number }): void {
    this.strategies.forEach((strategy, competitorId) => {
      // Check for market entry
      if (strategy.marketStatus === 'inactive' && strategy.marketEntryYear) {
        if (currentDate.year >= strategy.marketEntryYear) {
          console.log(`🚀 ${competitorId} is entering the market in ${currentDate.year}!`);
          strategy.marketStatus = 'active';
          
          // Trigger initial product development for entering companies
          this.startProductDevelopment(competitorId).catch(err => 
            console.error(`Failed to start product development for ${competitorId}:`, err)
          );
        }
      }
      
      // Check for market exit
      if (strategy.marketStatus === 'active' && strategy.marketExitYear) {
        if (currentDate.year >= strategy.marketExitYear) {
          console.log(`👋 ${competitorId} is exiting the market in ${currentDate.year}`);
          strategy.marketStatus = 'exited';
          
          // Discontinue all active products
          this.discontinueAllCompanyProducts(competitorId).catch(err =>
            console.error(`Failed to discontinue products for ${competitorId}:`, err)
          );
        }
      }
    });
  }
  
  /**
   * Discontinue all products for a company that is exiting the market
   */
  private async discontinueAllCompanyProducts(competitorId: string): Promise<void> {
    try {
      const result = await ProductManager.getAllProducts();
      if (!result.success || !result.data) return;
      
      const companyProducts = result.data.filter(product =>
        product.metadata?.companyId === competitorId &&
        product.status === 'released'
      );

      for (const product of companyProducts) {
        await this.discontinueCompetitorProduct(product.id, competitorId);
        console.log(`🚫 Discontinued ${product.name} due to company exit`);
      }
    } catch (error) {
      console.error(`Error discontinuing products for ${competitorId}:`, error);
    }
  }

  /**
   * Start AI product development pipeline
   * This allows AI competitors to follow the same development phases as players
   */
  public async startProductDevelopment(competitorId: string): Promise<void> {
    const strategy = this.strategies.get(competitorId);
    if (!strategy) return;

    // Gather market context for pricing decisions
    const marketContext = await this.gatherMarketContext();

    // Generate design for development with market awareness
    const designConfig = aiDesignGenerator.generateDesignConfiguration({
      id: competitorId,
      innovationFocus: strategy.innovationFocus,
      priceCompetitiveness: strategy.priceCompetitiveness,
      preferredSegments: strategy.preferredSegments,
    }, marketContext);

    const development: AIProductDevelopment = {
      competitorId,
      designConfig,
      phase: 'concept',
      startDate: Date.now(),
      estimatedDuration: 3 + Math.random() * 6, // 3-9 months like player products
      budget: strategy.rdBudget * (0.5 + Math.random() * 0.5), // 50-100% of R&D budget
      confidence: 0.7 + Math.random() * 0.3, // 70-100% confidence
      qualityScore: 70 + Math.random() * 30, // 70-100 quality score
    };

    const developments = this.activeProductDevelopments.get(competitorId) || [];
    developments.push(development);
    this.activeProductDevelopments.set(competitorId, developments);

    // console.log(`🔬 AI Competitor ${competitorId} started developing: ${designConfig.name}`);
  }

  /**
   * Progress AI product developments through phases
   * Called periodically by the game simulation
   */
  public async progressProductDevelopments(timeElapsed: number): Promise<void> {
    for (const [competitorId, developments] of this.activeProductDevelopments.entries()) {
      const updatedDevelopments: AIProductDevelopment[] = [];

      for (const development of developments) {
        const monthsElapsed = timeElapsed / (30 * 24 * 60 * 60 * 1000); // Convert to months
        const progressTime = (Date.now() - development.startDate) / (30 * 24 * 60 * 60 * 1000);

        if (progressTime >= development.estimatedDuration) {
          // Development phase completed
          await this.completeProductDevelopment(development);
        } else {
          // Continue development
          updatedDevelopments.push(development);
        }
      }

      this.activeProductDevelopments.set(competitorId, updatedDevelopments);
    }
  }

  /**
   * Complete AI product development and launch the product
   */
  private async completeProductDevelopment(development: AIProductDevelopment): Promise<void> {
    console.log(`🚀 AI Competitor ${development.competitorId} completed development: ${development.designConfig.name}`);
    
    // Launch the product using the developed design
    await this.launchCompetitorProductFromDevelopment(development);
  }

  /**
   * Launch a product from completed development
   */
  private async launchCompetitorProductFromDevelopment(development: AIProductDevelopment): Promise<void> {
    const competitorId = development.competitorId;
    const designConfig = development.designConfig;

    try {
      // Calculate metrics using the same system as player products
      const selectedComponents = {
        os: designConfig.os,
        cpu: designConfig.cpu,
        gpu: designConfig.gpu,
        ram: designConfig.ram,
        storage: designConfig.storage,
        network: designConfig.network,
        camera: designConfig.camera,
        battery: designConfig.battery,
      };

      const productionCost = ProductMetricsService.calculateTotalCost(selectedComponents);
      const techScore = ProductMetricsService.calculateTechScore(selectedComponents);
      const marketAppeal = ProductMetricsService.calculateValueScore(techScore, designConfig.price);

      // Apply development quality to the final product
      const qualityMultiplier = development.qualityScore / 100;
      const finalTechScore = Math.floor(techScore * qualityMultiplier);
      const finalMarketAppeal = Math.floor(marketAppeal * qualityMultiplier);

      // Create temporary product for review generation
      const tempProduct = {
        techScore: finalTechScore,
        marketAppeal: finalMarketAppeal,
        price: designConfig.price
      } as Product;

      // Generate reviews for competitor product
      const reviews = reviewGenerator.generateReviews(tempProduct);
      const averageRating = reviewGenerator.calculateAverageRating(reviews);

      // Create the real Product object
      const productData: Omit<Product, 'id'> = {
        name: designConfig.name,
        description: `AI-developed product by ${competitorId}`,
        developmentCost: development.budget,
        productionCost,
        price: designConfig.price,
        techScore: finalTechScore,
        marketAppeal: finalMarketAppeal,
        status: 'released',
        designConfig: {
          name: designConfig.name,
          price: designConfig.price,
          color: designConfig.color,
          material: designConfig.material,
          components: selectedComponents,
          design: {
            buttonColor: designConfig.buttonColor,
            hasButtons: designConfig.hasButtons,
            screenBorderColor: designConfig.screenBorderColor,
            cameraStyle: designConfig.cameraStyle,
            cameraColor: designConfig.cameraColor,
            cameraPosition: designConfig.cameraPosition,
            buttonPositions: designConfig.buttonPositions,
            phoneSize: designConfig.phoneSize,
            screenSize: designConfig.screenSize,
            cornerRadius: designConfig.cornerRadius,
          },
          techScore: finalTechScore,
          marketAppeal: finalMarketAppeal,
        },
        metadata: {
          companyId: competitorId,
          designDate: this.getCurrentGameDate(),
          releaseDate: this.getCurrentGameDate(),
          isReleased: true, // Competitors auto-release
        },
        salesMetrics: {
          unitsSold: 0,
          revenue: 0,
          averageRating: averageRating,
          marketShare: 0.01,
        },
        reviews,
        developmentPhase: {
          phase: 'manufacturing',
          startDate: {
            day: new Date(development.startDate).getDate(),
            month: new Date(development.startDate).getMonth() + 1,
            year: new Date(development.startDate).getFullYear(),
          },
          estimatedDuration: development.estimatedDuration,
          actualDuration: development.estimatedDuration,
          budget: development.budget,
          confidence: development.confidence,
          qualityScore: development.qualityScore,
          riskEvents: [],
        },
      };

      // Store in database
      const result = await ProductManager.createProduct(productData);

      if (result.success) {
        // Invalidate market share cache so it recalculates with new competitor
        marketShareService.invalidateCache();

        // console.log(`✅ AI Competitor ${competitorId} launched developed product: ${designConfig.name} (ID: ${result.data?.id})`);

        // Update internal tracking
        const competitorProduct: CompetitorProduct = {
          id: result.data!.id,
          competitorId,
          name: designConfig.name,
          price: designConfig.price,
          techScore: finalTechScore,
          marketShare: 0.01,
          launchDate: Date.now(),
          lifecycle: 'launch'
        };

        const products = this.competitorProducts.get(competitorId) || [];
        products.push(competitorProduct);
        this.competitorProducts.set(competitorId, products);
      } else {
        console.error(`❌ Failed to launch developed AI product for ${competitorId}:`, result.error);
      }
    } catch (error) {
      console.error(`❌ Error launching developed AI product for ${competitorId}:`, error);
    }
  }

  /**
   * Launch a seasonal product with custom release date (for initialization)
   */
  private async launchSeasonalProductWithCustomDate(competitorId: string, scheduledLaunch: any, customReleaseDate: { day: number; month: number; year: number }): Promise<void> {
    const strategy = this.strategies.get(competitorId);
    if (!strategy) return;

    try {
      // Generate design configuration with brand-specific characteristics
      const brandConfig = this.getBrandCharacteristics(competitorId, scheduledLaunch.productType);
      const designConfig = aiDesignGenerator.generateDesignConfiguration({
        id: competitorId,
        innovationFocus: strategy.innovationFocus,
        priceCompetitiveness: strategy.priceCompetitiveness,
        preferredSegments: strategy.preferredSegments,
      }, brandConfig);

      // Override the name with the scheduled name
      designConfig.name = scheduledLaunch.productName;

      // Apply brand-specific pricing and features
      this.applyBrandCharacteristics(designConfig, competitorId, scheduledLaunch.productType);

      // Calculate metrics
      const selectedComponents = {
        os: designConfig.os,
        cpu: designConfig.cpu,
        gpu: designConfig.gpu,
        ram: designConfig.ram,
        storage: designConfig.storage,
        network: designConfig.network,
        camera: designConfig.camera,
        battery: designConfig.battery,
      };

      const productionCost = ProductMetricsService.calculateTotalCost(selectedComponents);
      const techScore = ProductMetricsService.calculateTechScore(selectedComponents);
      const marketAppeal = ProductMetricsService.calculateValueScore(techScore, designConfig.price);

      // Create temporary product for review generation
      const tempProduct = {
        techScore,
        marketAppeal,
        price: designConfig.price
      } as Product;

      // Generate reviews for competitor product
      const reviews = reviewGenerator.generateReviews(tempProduct);
      const averageRating = reviewGenerator.calculateAverageRating(reviews);

      // Create product with custom release date
      const productData: Omit<Product, 'id'> = {
        name: designConfig.name,
        description: `${competitorId} flagship smartphone`,
        developmentCost: productionCost * 3, // Higher development costs for flagship products
        productionCost,
        price: designConfig.price,
        techScore,
        marketAppeal,
        status: 'released',
        designConfig: {
          name: designConfig.name,
          price: designConfig.price,
          color: designConfig.color,
          material: designConfig.material,
          components: selectedComponents,
          design: {
            buttonColor: designConfig.buttonColor,
            hasButtons: designConfig.hasButtons,
            screenBorderColor: designConfig.screenBorderColor,
            cameraStyle: designConfig.cameraStyle,
            cameraColor: designConfig.cameraColor,
            cameraPosition: designConfig.cameraPosition,
            buttonPositions: designConfig.buttonPositions,
            phoneSize: designConfig.phoneSize,
            screenSize: designConfig.screenSize,
            cornerRadius: designConfig.cornerRadius,
          },
          techScore,
          marketAppeal,
        },
        metadata: {
          companyId: competitorId,
          designDate: customReleaseDate,
          releaseDate: customReleaseDate, // Use custom date instead of current game date
          isReleased: true, // AI competitors auto-release their products (kept for backwards compatibility)
        },
        salesMetrics: {
          unitsSold: 0,
          revenue: 0,
          averageRating,
          marketShare: 0.05, // Start with larger market share for major brands
        },
        reviews,
      };

      const result = await ProductManager.createProduct(productData);

      if (result.success) {
        // Invalidate market share cache so it recalculates with new competitor
        marketShareService.invalidateCache();

        // console.log(`📱 ${competitorId} created initial product: ${scheduledLaunch.productName} (ID: ${result.data?.id}) - Release: ${customReleaseDate.month}/${customReleaseDate.year}`);

        // Update internal tracking
        const competitorProduct: CompetitorProduct = {
          id: result.data!.id,
          competitorId,
          name: scheduledLaunch.productName,
          price: designConfig.price,
          techScore,
          marketShare: 0.05,
          launchDate: new Date(customReleaseDate.year, customReleaseDate.month - 1, customReleaseDate.day).getTime(),
          lifecycle: 'launch'
        };

        const products = this.competitorProducts.get(competitorId) || [];
        products.push(competitorProduct);
        this.competitorProducts.set(competitorId, products);
      } else {
        console.error(`❌ Failed to create initial product for ${competitorId}:`, result.error);
      }
    } catch (error) {
      console.error(`❌ Error creating initial product for ${competitorId}:`, error);
    }
  }

  /**
   * Launch a seasonal product with brand-appropriate naming and characteristics
   */
  private async launchSeasonalProduct(competitorId: string, scheduledLaunch: any): Promise<void> {
    const strategy = this.strategies.get(competitorId);
    if (!strategy) return;

    try {
      // Gather market context for pricing decisions
      const marketContext = await this.gatherMarketContext();
      
      // Generate design configuration with brand-specific characteristics and market awareness
      const brandConfig = this.getBrandCharacteristics(competitorId, scheduledLaunch.productType);
      const designConfig = aiDesignGenerator.generateDesignConfiguration({
        id: competitorId,
        innovationFocus: strategy.innovationFocus,
        priceCompetitiveness: strategy.priceCompetitiveness,
        preferredSegments: strategy.preferredSegments,
      }, { ...brandConfig, ...marketContext });

      // Override the name with the scheduled name
      designConfig.name = scheduledLaunch.productName;

      // Apply brand-specific pricing and features
      this.applyBrandCharacteristics(designConfig, competitorId, scheduledLaunch.productType);

      // Calculate metrics
      const selectedComponents = {
        os: designConfig.os,
        cpu: designConfig.cpu,
        gpu: designConfig.gpu,
        ram: designConfig.ram,
        storage: designConfig.storage,
        network: designConfig.network,
        camera: designConfig.camera,
        battery: designConfig.battery,
      };

      const productionCost = ProductMetricsService.calculateTotalCost(selectedComponents);
      const techScore = ProductMetricsService.calculateTechScore(selectedComponents);
      const marketAppeal = ProductMetricsService.calculateValueScore(techScore, designConfig.price);

      // Create temporary product for review generation
      const tempProduct = {
        techScore,
        marketAppeal,
        price: designConfig.price
      } as Product;

      // Generate reviews for competitor product
      const reviews = reviewGenerator.generateReviews(tempProduct);
      const averageRating = reviewGenerator.calculateAverageRating(reviews);

      // Create product
      const productData: Omit<Product, 'id'> = {
        name: designConfig.name,
        description: `${competitorId} flagship smartphone`,
        developmentCost: productionCost * 3, // Higher development costs for flagship products
        productionCost,
        price: designConfig.price,
        techScore,
        marketAppeal,
        status: 'released',
        designConfig: {
          name: designConfig.name,
          price: designConfig.price,
          color: designConfig.color,
          material: designConfig.material,
          components: selectedComponents,
          design: {
            buttonColor: designConfig.buttonColor,
            hasButtons: designConfig.hasButtons,
            screenBorderColor: designConfig.screenBorderColor,
            cameraStyle: designConfig.cameraStyle,
            cameraColor: designConfig.cameraColor,
            cameraPosition: designConfig.cameraPosition,
            buttonPositions: designConfig.buttonPositions,
            phoneSize: designConfig.phoneSize,
            screenSize: designConfig.screenSize,
            cornerRadius: designConfig.cornerRadius,
          },
          techScore,
          marketAppeal,
        },
        metadata: {
          companyId: competitorId,
          designDate: this.getCurrentGameDate(),
          releaseDate: this.getCurrentGameDate(), // Use proper game date for seasonal launches
          isReleased: true, // AI competitors auto-release their products (kept for backwards compatibility)
        },
        salesMetrics: {
          unitsSold: 0,
          revenue: 0,
          averageRating,
          marketShare: 0.05, // Start with larger market share for major brands
        },
        reviews,
      };

      const result = await ProductManager.createProduct(productData);

      if (result.success) {
        // Invalidate market share cache so it recalculates with new competitor
        marketShareService.invalidateCache();

        // console.log(`📱 ${competitorId} launched seasonal product: ${scheduledLaunch.productName} (ID: ${result.data?.id})`);

        // Update internal tracking
        const competitorProduct: CompetitorProduct = {
          id: result.data!.id,
          competitorId,
          name: scheduledLaunch.productName,
          price: designConfig.price,
          techScore,
          marketShare: 0.05,
          launchDate: Date.now(),
          lifecycle: 'launch'
        };

        const products = this.competitorProducts.get(competitorId) || [];
        products.push(competitorProduct);
        this.competitorProducts.set(competitorId, products);
      } else {
        console.error(`❌ Failed to launch seasonal product for ${competitorId}:`, result.error);
      }
    } catch (error) {
      console.error(`❌ Error launching seasonal product for ${competitorId}:`, error);
    }
  }

  /**
   * Get brand-specific characteristics for product generation
   */
  private getBrandCharacteristics(competitorId: string, productType: string): any {
    const characteristics = {
      playerAverageTechScore: 85,
      playerAveragePrice: 999,
      emergingTrends: []
    };

    switch (competitorId) {
      case 'apple':
        characteristics.playerAverageTechScore = 90;
        characteristics.playerAveragePrice = 1199;
        characteristics.emergingTrends = ['premium_materials', 'ecosystem_integration'];
        break;
      case 'samsung':
        characteristics.playerAverageTechScore = 88;
        characteristics.playerAveragePrice = 999;
        if (productType === 'galaxy_fold') {
          characteristics.emergingTrends = ['foldable_display', 'innovation_focus'];
          characteristics.playerAveragePrice = 1799;
        } else {
          characteristics.emergingTrends = ['display_innovation', 'camera_excellence'];
        }
        break;
      case 'google':
        characteristics.playerAverageTechScore = 85;
        characteristics.playerAveragePrice = 799;
        characteristics.emergingTrends = ['ai_integration', 'software_optimization'];
        break;
    }

    return characteristics;
  }

  /**
   * Apply brand-specific characteristics to design configuration
   */
  private applyBrandCharacteristics(designConfig: DesignConfiguration, competitorId: string, productType: string): void {
    switch (competitorId) {
      case 'apple':
        // Apple characteristics: premium pricing, refined design
        designConfig.price = Math.max(799, designConfig.price * 1.1);
        designConfig.material = Math.random() > 0.5 ? 'metal' : 'ceramic';
        designConfig.cornerRadius = 0.3; // More rounded corners
        break;
        
      case 'samsung':
        if (productType === 'galaxy_fold') {
          // Galaxy Fold: premium foldable pricing
          designConfig.price = Math.max(1299, designConfig.price * 1.5);
          designConfig.material = 'metal';
        } else {
          // Regular Galaxy: competitive pricing
          designConfig.price = Math.max(599, designConfig.price * 1.05);
        }
        designConfig.cameraStyle = 'square'; // Samsung camera island style
        break;
        
      case 'google':
        // Google: competitive pricing, software focus
        designConfig.price = Math.max(499, designConfig.price * 0.95);
        designConfig.cameraStyle = 'pill'; // Google camera bar style
        break;
    }
  }

  /**
   * Get active AI product developments (for debugging/admin purposes only)
   */
  public getActiveProductDevelopments(): Map<string, AIProductDevelopment[]> {
    return this.activeProductDevelopments;
  }

  /**
   * Evaluate products for discontinuation based on lifecycle and performance
   */
  private async evaluateProductDiscontinuation(): Promise<CompetitorAction[]> {
    const discontinuationActions: CompetitorAction[] = [];
    const currentDate = this.getCurrentGameDate();
    
    // Process all competitors in parallel
    const promises = Array.from(this.strategies.entries()).map(([competitorId, strategy]) => 
      this.evaluateCompetitorProductsForDiscontinuation(competitorId, strategy)
    );
    
    const results = await Promise.all(promises);
    results.forEach(actions => discontinuationActions.push(...actions));
    
    return discontinuationActions;
  }

  /**
   * Evaluate a specific competitor's products for discontinuation
   */
  private async evaluateCompetitorProductsForDiscontinuation(
    competitorId: string, 
    strategy: CompetitorStrategy
  ): Promise<CompetitorAction[]> {
    const actions: CompetitorAction[] = [];
    
    try {
      // Get all products from database
      const result = await ProductManager.getAllProducts();
      if (!result.success || !result.data) return actions;
      
      // Filter for this competitor's active products
      const competitorProducts = result.data.filter(product =>
        product.metadata?.companyId === competitorId &&
        product.status === 'released'
      );

      if (competitorProducts.length === 0) return actions;
      
      // Sort by release date (oldest first)
      competitorProducts.sort((a, b) => {
        const dateA = a.metadata?.releaseDate;
        const dateB = b.metadata?.releaseDate;
        if (!dateA || !dateB) return 0;
        
        const timeA = new Date(dateA.year, dateA.month - 1, dateA.day).getTime();
        const timeB = new Date(dateB.year, dateB.month - 1, dateB.day).getTime();
        return timeA - timeB;
      });
      
      // Define discontinuation thresholds by company
      const companyThresholds = {
        apple: { minMonths: 36, declineThreshold: 0.4 }, // Conservative - premium focus
        samsung: { minMonths: 24, declineThreshold: 0.5 }, // Moderate - diverse portfolio
        google: { minMonths: 18, declineThreshold: 0.6 }, // Aggressive - software focus
        lg: { minMonths: 30, declineThreshold: 0.45 }, // Moderate-conservative
        nokia: { minMonths: 36, declineThreshold: 0.35 }, // Conservative - legacy focus
        palm: { minMonths: 18, declineThreshold: 0.7 }, // Very aggressive - startup mentality
        motorola: { minMonths: 30, declineThreshold: 0.4 }, // Conservative - established brand
        microsoft: { minMonths: 24, declineThreshold: 0.65 }, // Aggressive - late entrant
        rim: { minMonths: 42, declineThreshold: 0.3 } // Very conservative - enterprise focus
      };
      
      const threshold = companyThresholds[competitorId as keyof typeof companyThresholds] || 
                       { minMonths: 24, declineThreshold: 0.5 };
      
      for (const product of competitorProducts) {
        const shouldDiscontinue = await this.shouldDiscontinueProduct(
          product, 
          competitorId, 
          threshold,
          competitorProducts
        );
        
        if (shouldDiscontinue) {
          // Don't discontinue if it's the only active product
          const otherActiveProducts = competitorProducts.filter(p =>
            p.id !== product.id && p.status === 'released'
          );
          
          if (otherActiveProducts.length > 0) {
            await this.discontinueCompetitorProduct(product.id, competitorId);
            
            actions.push({
              competitorId,
              action: 'product_discontinuation',
              productName: product.name,
              probability: 1.0,
              marketImpact: -0.1, // Small negative impact
              timestamp: Date.now()
            });
            
            console.log(`🚫 ${this.getCompanyDisplayName(competitorId)} discontinued ${product.name}`);
            
            // Check if we need to start developing a new product
            if (otherActiveProducts.length <= 1 && strategy.innovationFocus > 0.5) {
              console.log(`🔬 ${competitorId} starting new product development (low product count)`);
              await this.startProductDevelopment(competitorId);
            }
          } else {
            console.log(`⚠️ ${competitorId} would discontinue ${product.name} but it's their only product`);
            // Force new product development if stuck with only one old product
            console.log(`🔬 ${competitorId} starting emergency product development`);
            await this.startProductDevelopment(competitorId);
          }
        }
      }
    } catch (error) {
      console.error(`Error evaluating products for discontinuation: ${error}`);
    }
    
    return actions;
  }

  /**
   * Determine if a product should be discontinued
   */
  private async shouldDiscontinueProduct(
    product: Product,
    competitorId: string,
    threshold: { minMonths: number; declineThreshold: number },
    allCompetitorProducts: Product[]
  ): Promise<boolean> {
    const releaseDate = product.metadata?.releaseDate;
    if (!releaseDate) return false;
    
    const currentDate = this.getCurrentGameDate();
    const releaseDateObj = new Date(releaseDate.year, releaseDate.month - 1, releaseDate.day);
    const currentDateObj = new Date(currentDate.year, currentDate.month - 1, currentDate.day);
    
    const monthsSinceRelease = (currentDateObj.getTime() - releaseDateObj.getTime()) / (1000 * 60 * 60 * 24 * 30);
    
    // Check if product is old enough to consider discontinuation
    if (monthsSinceRelease < threshold.minMonths) {
      return false;
    }
    
    // Calculate lifecycle stage
    const lifecycleStage = this.getProductLifecycleStage(monthsSinceRelease);
    
    // Only discontinue products in decline phase
    if (lifecycleStage !== 'decline') {
      return false;
    }
    
    // Check if there's a newer product in the same price segment
    const priceSegment = this.getProductPriceSegment(product.price);
    const newerProductInSegment = allCompetitorProducts.some(p => {
      if (p.id === product.id || !p.metadata?.releaseDate) return false;
      
      const otherReleaseDate = new Date(
        p.metadata.releaseDate.year, 
        p.metadata.releaseDate.month - 1, 
        p.metadata.releaseDate.day
      );
      
      return this.getProductPriceSegment(p.price) === priceSegment && 
             otherReleaseDate > releaseDateObj;
    });
    
    // More likely to discontinue if there's a replacement
    if (newerProductInSegment) {
      return monthsSinceRelease >= threshold.minMonths;
    }
    
    // Otherwise, only discontinue if very old
    return monthsSinceRelease >= threshold.minMonths * 1.5;
  }

  /**
   * Get product lifecycle stage based on months since release
   */
  private getProductLifecycleStage(monthsSinceRelease: number): string {
    if (monthsSinceRelease < 3) return 'launch';
    if (monthsSinceRelease < 12) return 'growth';
    if (monthsSinceRelease < 24) return 'maturity';
    return 'decline';
  }

  /**
   * Get product price segment
   */
  private getProductPriceSegment(price: number): string {
    if (price <= 300) return 'budget';
    if (price <= 700) return 'mainstream';
    if (price <= 1200) return 'premium';
    return 'flagship';
  }

  /**
   * Discontinue a competitor product
   */
  private async discontinueCompetitorProduct(productId: string, competitorId: string): Promise<void> {
    try {
      const currentDate = this.getCurrentGameDate();
      
      // First get the existing product to preserve its metadata
      const existingProduct = await ProductManager.getProduct(productId);
      if (!existingProduct.success || !existingProduct.data) {
        console.error(`Failed to get product ${productId} for discontinuation`);
        return;
      }
      
      // Update product status to discontinued
      const updateResult = await ProductManager.updateProduct(productId, {
        status: 'discontinued',
        metadata: {
          ...existingProduct.data.metadata,
          discontinuedDate: currentDate
        }
      });
      
      if (updateResult.success) {
        // Update internal tracking
        const products = this.competitorProducts.get(competitorId) || [];
        const productIndex = products.findIndex(p => p.id === productId);
        
        if (productIndex >= 0) {
          // Update lifecycle to discontinued
          products[productIndex].lifecycle = 'decline'; // We don't have 'discontinued' in the type
          // Keep the product in tracking array but marked as discontinued
          this.competitorProducts.set(competitorId, products);
        }
      }
    } catch (error) {
      console.error(`Failed to discontinue product ${productId}:`, error);
    }
  }

  /**
   * Simulate daily sales for all competitor products
   * @deprecated This method is replaced by the unified simulateCompetitorSales() which uses SalesCalculationService
   */
  // private async simulateCompetitorProductSales(): Promise<void> {
  //   // Sales simulation is now handled by the public simulateCompetitorSales() method
  //   // which is called daily from the game loop and uses the same SalesCalculationService
  //   // as player products to ensure fair competition and consistent market dynamics
  //   return;
  // }

  /**
   * Manually trigger a competitor product launch (for testing/debugging)
   */
  public async forceCompetitorProductLaunch(competitorId: string): Promise<void> {
    console.log(`🔧 Manually triggering product launch for ${competitorId}`);
    await this.launchCompetitorProduct(competitorId);
  }

  private hasInitialized = false; // Prevent multiple initializations

  /**
   * Reset the competitor AI for a new game
   */
  public resetForNewGame(): void {
    // console.log('🔄 Resetting Competitor AI for new game...');
    
    // Clear all internal state
    this.hasInitialized = false;
    this.strategies.clear();
    this.competitorProducts.clear();
    this.pendingActions = [];
    this.marketIntelligence.clear();
    this.activeProductDevelopments.clear();
    
    // Reset game date to default
    this.currentGameDate = { 
      day: GAME_CONFIG.STARTING_DAY, 
      month: GAME_CONFIG.STARTING_MONTH, 
      year: GAME_CONFIG.STARTING_YEAR 
    };
    
    // Re-initialize strategies and products
    this.initializeCompetitorStrategies();
    this.initializeCompetitorProducts();
    
    // Reset AI design generator
    aiDesignGenerator.setGameYear(this.currentGameDate.year);
    
    // console.log('✅ Competitor AI reset complete');
  }

  /**
   * Initialize some starting competitor products for testing
   * Creates products with proper seasonal release dates
   */
  public async initializeStartingProducts(): Promise<void> {
    // Prevent multiple calls during the same session
    if (this.hasInitialized) {
      // console.log('⏭️ Competitor AI already initialized, skipping...');
      return;
    }

    // console.log('🚀 Competitive AI: Creating starting competitor products with seasonal dates...');
    // console.log(`🚀 Current game date: ${this.currentGameDate.month}/${this.currentGameDate.year}`);
    
    try {
      // Get all existing products from database
      const result = await ProductManager.getAllProducts();
      if (!result.success || !result.data) {
        console.log('❌ Failed to get products from database:', result.error);
        return;
      }
      
      const allProducts = result.data;
      // console.log(`📊 Total products in database: ${allProducts.length}`);
      
      // Check if there are already competitor products
      const existingCompetitorProducts = allProducts.filter(p => 
        p.metadata?.companyId && 
        p.metadata.companyId !== 'player'
      );

      if (existingCompetitorProducts.length > 0) {
        console.log(`⏭️ Found ${existingCompetitorProducts.length} existing competitor products, skipping initialization`);
        existingCompetitorProducts.forEach(p => {
          console.log(`  📱 ${p.metadata?.companyId}: ${p.name}`);
        });
        this.hasInitialized = true;
        return;
      }

      // console.log(`🏢 Available competitors: [${Array.from(this.strategies.keys()).join(', ')}]`);

      // Create initial products with proper seasonal release dates - only if database is empty
      // Only create products for companies that are currently active
      const activeCompetitors = Array.from(this.strategies.entries())
        .filter(([_, strategy]) => strategy.marketStatus === 'active')
        .map(([id, _]) => id);
      
      // console.log(`🏢 Active competitors at start: [${activeCompetitors.join(', ')}]`);
      
      const seasonalLaunches = [
        { competitorId: 'samsung', month: 2, productType: 'galaxy', productName: 'Galaxy S' },
        { competitorId: 'lg', month: 4, productType: 'g', productName: 'G' },
        { competitorId: 'nokia', month: 9, productType: 'n', productName: 'N70' },
        { competitorId: 'palm', month: 6, productType: 'pre', productName: 'Pre' },
        { competitorId: 'motorola', month: 3, productType: 'razr', productName: 'RAZR' },
        { competitorId: 'microsoft', month: 10, productType: 'windows_phone', productName: 'Windows Phone 7' },
        { competitorId: 'rim', month: 11, productType: 'blackberry', productName: 'Bold' }
      ].filter(launch => activeCompetitors.includes(launch.competitorId));

      for (const launch of seasonalLaunches) {
        try {
          // Determine launch year and announcement timing
          let launchYear = this.currentGameDate.year;
          const announcementDate = this.currentGameDate;
          
          // If current month is past the launch month, schedule for next year
          if (this.currentGameDate.month > launch.month) {
            launchYear = this.currentGameDate.year + 1;
          }
          
          // Show product 1 month before release date
          const releaseMonth = launch.month;
          const announcementMonth = releaseMonth === 1 ? 12 : releaseMonth - 1;
          const announcementYear = releaseMonth === 1 ? launchYear - 1 : launchYear;
          
          // Only create product if it should be announced by now
          const shouldShowProduct = (
            this.currentGameDate.year > announcementYear || 
            (this.currentGameDate.year === announcementYear && this.currentGameDate.month >= announcementMonth)
          );
          
          if (!shouldShowProduct) {
            // console.log(`⏳ ${launch.productName} not yet announced (will announce ${announcementMonth}/${announcementYear})`);
            continue;
          }
          
          // console.log(`🚀 Creating ${launch.productName} for ${launch.competitorId} (announced ${announcementMonth}/${announcementYear}, releases ${launch.month}/${launchYear})`);
          
          // Create with proper seasonal release date
          const seasonalReleaseDate = {
            day: 1,
            month: launch.month,
            year: launchYear
          };
          
          await this.launchSeasonalProductWithCustomDate(launch.competitorId, {
            productType: launch.productType,
            productName: launch.productName,
            year: this.currentGameDate.year,
            month: launch.month,
            hasLaunched: false
          }, seasonalReleaseDate);
          
          // console.log(`✅ Created ${launch.productName} for ${launch.competitorId}`);
        } catch (error) {
          console.error(`❌ Failed to create ${launch.productName} for ${launch.competitorId}:`, error);
        }
      }
      
      this.hasInitialized = true;
      // console.log('✅ Competitor AI initialization completed');
    } catch (error) {
      console.error('❌ Failed to initialize starting products:', error);
    }
  }

  /**
   * Reset initialization flag (for testing)
   */
  public resetInitialization(): void {
    this.hasInitialized = false;
    // console.log('🔄 Competitor AI initialization flag reset');
  }

  public async simulateCompetitorSales(gameDate: { day: number; month: number; year: number }): Promise<void> {
    try {
      // Get all competitor products from database
      const result = await ProductManager.getAllProducts();
      if (!result.success || !result.data) {
        console.log('🔍 [CompetitorAI] simulateCompetitorSales: Failed to get products from database');
        return;
      }

      const competitorProducts = result.data.filter(product =>
        product.metadata?.companyId &&
        product.metadata.companyId !== 'player' &&
        product.status === 'released'
      );

      // Before calculating sales, validate data
      competitorProducts.forEach(product => {

        // Validate and fix releaseDate if needed
        if (product.metadata?.releaseDate) {
          const releaseDate = product.metadata.releaseDate;
          // Check if releaseDate is malformed
          if (typeof releaseDate !== 'object' ||
              typeof releaseDate.year !== 'number' ||
              typeof releaseDate.month !== 'number') {
            console.warn(`[CompetitorAI] Invalid releaseDate for ${product.name}:`, releaseDate);
            // Set a default releaseDate to current date
            product.metadata.releaseDate = this.currentGameDate;
            console.log(`[CompetitorAI] Fixed releaseDate for ${product.name} to current date`);
          }
        } else if (product.metadata) {
          // If no releaseDate at all, set to current date
          console.warn(`[CompetitorAI] No releaseDate for ${product.name}, setting to current date`);
          product.metadata.releaseDate = this.currentGameDate;
        }
      });

      // console.log(`🔍 [CompetitorAI] simulateCompetitorSales: Found ${competitorProducts.length} competitor products eligible for sales`);
      if (competitorProducts.length > 0) {
        // console.log(`  - Sample: ${competitorProducts[0].name} (${competitorProducts[0].metadata?.companyId}) - Current units: ${competitorProducts[0].salesMetrics?.unitsSold || 0}`);
      }

      // Use the same sales calculation service as player products
      const salesService = SalesCalculationService.getInstance();
      const marketCapacityService = MarketCapacityService.getInstance();

      // Group products by company to calculate with proper reputation
      const productsByCompany = new Map<string, Product[]>();
      for (const product of competitorProducts) {
        const companyId = product.metadata.companyId;
        if (!productsByCompany.has(companyId)) {
          productsByCompany.set(companyId, []);
        }
        productsByCompany.get(companyId)!.push(product);
      }

      // Calculate sales for each company's products with their specific reputation
      for (const [companyId, companyProducts] of productsByCompany) {
        const companyReputation = this.getCompanyReputation(companyId);
        const companyMarketShare = await this.getCompanyMarketShare(companyId);
        
        // Calculate sales using the same service as player products
        // Create mock inventory map with unlimited stock for competitors
        const mockInventoryMap = new Map<string, number>();
        companyProducts.forEach(product => {
          mockInventoryMap.set(product.id, 999999); // Unlimited inventory
        });

        const salesMap = salesService.batchCalculateSales(
          companyProducts,
          gameDate,  // Use gameDate directly instead of currentDate
          companyReputation,
          companyMarketShare,
          mockInventoryMap // Pass the mock inventory
        );

        // Update sales for each product
        let totalDailySales = 0;
        for (const product of companyProducts) {
          const salesBreakdown = salesMap.get(product.id);
          if (!salesBreakdown) {
            console.log(`  ❌ No sales breakdown for ${product.name}`);
            continue;
          }
          // console.log(`  📊 Sales breakdown for ${product.name}: dailySales=${salesBreakdown.dailySales}`);

          const dailySales = salesBreakdown.dailySales;
          totalDailySales += dailySales;

          // Record sales to market capacity tracking (same as player products)
          if (dailySales > 0) {
            const segmentName = salesService.getProductSegment(product);
            marketCapacityService.recordSales(segmentName, dailySales, gameDate.year);
          }

          // Update sales metrics
          const newUnitsSold = (product.salesMetrics?.unitsSold || 0) + dailySales;
          const newRevenue = (product.salesMetrics?.revenue || 0) + (dailySales * product.price);

          const updateResult = await ProductManager.updateProduct(product.id, {
            salesMetrics: {
              unitsSold: newUnitsSold,
              revenue: newRevenue,
              averageRating: product.salesMetrics?.averageRating || 4.0,
              marketShare: 0 // Will be calculated separately
            }
          });

          if (dailySales > 0) {
            // console.log(`  💰 ${product.name}: +${dailySales} sales (total: ${newUnitsSold}) - Update ${updateResult.success ? 'succeeded' : 'FAILED'}`);
          }
        }
        if (totalDailySales > 0) {
          // console.log(`  📊 ${companyId}: Total daily sales = ${totalDailySales}`);
        }
      }
    } catch (error) {
      console.error('Error simulating competitor sales:', error);
    }
  }

  private getCompanyReputation(companyId: string): number {
    // Return reputation based on company
    const reputations: { [key: string]: number } = {
      'apple': 90,
      'samsung': 85,
      'google': 80,
      'lg': 75,
      'nokia': 70,
      'palm': 65,
      'motorola': 75,
      'microsoft': 80,
      'rim': 70
    };
    return reputations[companyId] || 50;
  }

  private async getCompanyMarketShare(companyId: string): Promise<number> {
    // Get the company's current market share from their products
    try {
      const result = await ProductManager.getAllProducts();
      if (!result.success || !result.data) return 0.05; // Default 5% market share
      
      const allProducts = result.data.filter(p => p.status === 'released' && p.salesMetrics?.unitsSold > 0);
      const companyProducts = allProducts.filter(p => p.metadata?.companyId === companyId);
      
      if (allProducts.length === 0) return 0.05;
      
      const totalUnits = allProducts.reduce((sum, p) => sum + (p.salesMetrics?.unitsSold || 0), 0);
      const companyUnits = companyProducts.reduce((sum, p) => sum + (p.salesMetrics?.unitsSold || 0), 0);
      
      return totalUnits > 0 ? companyUnits / totalUnits : 0.05;
    } catch (error) {
      console.error(`Failed to calculate market share for ${companyId}:`, error);
      return 0.05;
    }
  }

  private getCompanyDisplayName(companyId: string): string {
    const displayNames: { [key: string]: string } = {
      'apple': 'Apple',
      'samsung': 'Samsung',
      'google': 'Google',
      'lg': 'LG',
      'nokia': 'Nokia',
      'palm': 'Palm',
      'motorola': 'Motorola',
      'microsoft': 'Microsoft',
      'rim': 'RIM'
    };
    return displayNames[companyId] || companyId;
  }

  public async calculateMarketShares(): Promise<void> {
    try {
      // Get all products from database
      const result = await ProductManager.getAllProducts();
      if (!result.success || !result.data) return;

      const allProducts = result.data.filter(product => 
        product.status !== 'discontinued' && 
        (product.salesMetrics?.unitsSold || 0) > 0
      );

      // Calculate total units sold across all products
      const totalUnitsSold = allProducts.reduce((total, product) => 
        total + (product.salesMetrics?.unitsSold || 0), 0
      );

      if (totalUnitsSold === 0) return; // No sales yet

      // Update market share for each product
      for (const product of allProducts) {
        const unitsSold = product.salesMetrics?.unitsSold || 0;
        const marketShare = (unitsSold / totalUnitsSold) * 100;

        await ProductManager.updateProduct(product.id, {
          salesMetrics: {
            ...product.salesMetrics,
            marketShare: Math.round(marketShare * 100) / 100 // Round to 2 decimal places
          }
        });
      }

      console.log(`📊 Market shares updated for ${allProducts.length} products (Total units: ${totalUnitsSold.toLocaleString()})`);
    } catch (error) {
      console.error('Error calculating market shares:', error);
    }
  }

  /**
   * Gather market context for AI pricing decisions
   */
  private async gatherMarketContext(): Promise<{
    playerProducts?: Array<{ price: number; techScore: number; marketShare?: number }>;
    competitorProducts?: Array<{ price: number; techScore: number; competitorId: string }>;
    marketSaturation?: number;
    economicConditions?: number;
  }> {
    const context: {
      playerProducts?: Array<{ price: number; techScore: number; marketShare?: number }>;
      competitorProducts?: Array<{ price: number; techScore: number; competitorId: string }>;
      marketSaturation?: number;
      economicConditions?: number;
    } = {};

    try {
      // Get all products from the database
      const result = await ProductManager.getAllProducts();
      if (result.success && result.data) {
        const activeProducts = result.data.filter(p =>
          p.status === 'released' &&
          p.metadata?.isDiscontinued !== true
        );

        // Separate player and competitor products
        context.playerProducts = activeProducts
          .filter(p => !p.metadata?.companyId || p.metadata.companyId === 'player')
          .map(p => ({
            price: p.price,
            techScore: p.techScore,
            marketShare: p.metadata?.marketShare || 0
          }));

        context.competitorProducts = activeProducts
          .filter(p => p.metadata?.companyId && p.metadata.companyId !== 'player')
          .map(p => ({
            price: p.price,
            techScore: p.techScore,
            competitorId: p.metadata?.companyId || ''
          }));
      }

      // Calculate market saturation based on total products
      const totalProducts = (context.playerProducts?.length || 0) + (context.competitorProducts?.length || 0);
      context.marketSaturation = Math.min(1, totalProducts / 50); // Assume 50 products = saturated market

      // Simulate economic conditions (could be enhanced with actual game economy data)
      const gameYear = this.currentGameDate.year;
      const yearsSinceStart = gameYear - GAME_CONFIG.STARTING_YEAR;
      
      // Economic cycles: boom -> recession -> recovery -> boom
      const economicCycle = Math.sin((yearsSinceStart / 5) * Math.PI * 2) * 0.3 + 0.6;
      context.economicConditions = Math.max(0.1, Math.min(1, economicCycle));

    } catch (error) {
      console.error('Error gathering market context:', error);
    }

    return context;
  }
}

export const competitorAI = CompetitorAIService.getInstance();

// Debug helpers for browser console
(window as any).debugCompetitorAI = {
  async launchProduct(competitorId: string) {
    console.log(`🔧 Debug: Launching product for ${competitorId}`);
    await competitorAI.forceCompetitorProductLaunch(competitorId);
  },
  
  async initStartingProducts() {
    console.log('🔧 Debug: Initializing starting products');
    await competitorAI.initializeStartingProducts();
  },
  
  getStrategies() {
    console.log('🔧 Debug: Current competitor strategies:', competitorAI.getCompetitorStrategies());
    return competitorAI.getCompetitorStrategies();
  },
  
  getProducts() {
    console.log('🔧 Debug: Current competitor products:', competitorAI.getCompetitorProducts());
    return competitorAI.getCompetitorProducts();
  }
};